// The urls a screen and its admin use, in a file with nothing else in it.
//
// Separate from routes.ts for one mechanical reason: api/src/rateLimit.ts has to
// name every one of them, and importing routes.ts from there would pull the
// signer, the database and the schedule reader into the limiter's import graph.
// PUBLISH_ENDPOINTS now lives in publish/endpoints.ts for the same reason.
//
// Not in the frozen sync contract, for the reason PUBLISH_ENDPOINTS gives: no
// enrolled device calls them. A screen is not an enrolled device — see migration
// 111 for why — and what it speaks is this file plus the signed documents in
// document.ts, versioned by the kiosk contract number rather than by the sync
// protocol's.
//
// Flat paths with one parameter between them, so that a rate limit rule names a
// url Fastify will recognise. `screen` takes its activation as a query
// parameter rather than as a path segment for exactly that reason: a limiter
// keyed on `/v1/kiosk/screen/:activation_id` and a route registered as
// `/v1/kiosk/screen/:activation` are two spellings of one url and
// `unlimitedRoutes` is the only thing that would ever notice.

export const KIOSK_ENDPOINTS = {
  /** Sign and store a schedule. nova_admin, admin token. */
  publishSchedule: "/v1/admin/kiosk/schedules",
  /** Put a still picture where a screen can fetch it. nova_admin, admin token. */
  uploadAsset: "/v1/admin/kiosk/assets",
  /**
   * Plan a film upload. nova_admin, admin token. The bytes themselves go to
   * the bucket, not through this body: a film is tens of megabytes and this
   * service's picture door refuses one for that reason.
   */
  startFilm: "/v1/admin/kiosk/films",
  /** Confirm a planned film is in the bucket. nova_admin, admin token. */
  finishFilm: "/v1/admin/kiosk/films/finish",
  /**
   * Plan a large still upload. nova_admin, admin token. The bytes go to the
   * same bucket films use; the citation a schedule signs is still an asset path.
   */
  startPicture: "/v1/admin/kiosk/pictures",
  /** Confirm a planned large still is in the bucket. nova_admin session. */
  finishPicture: "/v1/admin/kiosk/pictures/finish",
  /** Mint a view or pull token for one activation. nova_admin, admin token. */
  issueScreenToken: "/v1/admin/kiosk/screen-tokens",
  /** Mint a one-hour, session-bound view token for Console previews. */
  issuePreviewToken: "/v1/admin/kiosk/preview-token",
  /**
   * Which browsers have spoken for one activation, and the trouble count each
   * last sent. nova_admin session, like listing clients — a diagnostic read, not
   * a publish, so it does not take a fresh factor and it does not wait on a
   * signing key.
   */
  seenScreens: "/v1/admin/kiosk/screens",
  /**
   * The working rotation for one activation. A signed-in nova_admin or
   * client_viewer, like opening Content. GET reads it; PUT records a new row.
   * Not a publish: it does not take a fresh factor and it does not wait on a
   * signing key. A screen never reads this path.
   */
  playlist: "/v1/admin/kiosk/playlist",
  /**
   * The show's board record (157, decision C11): places, window, names, open
   * places and resetting, shared by Board › Setup and the kiosk's leaderboard
   * piece. GET is read like the playlist, a Viewer included for a granted
   * show; PUT saves, an Admin's or an Editor's. Not a publish.
   */
  board: "/v1/admin/kiosk/board",
  /** Reset now: the board counts from this moment. An Admin's or an Editor's. */
  boardClear: "/v1/admin/kiosk/board/clear",
  /**
   * What is currently pulled off this activation's walls, and the desk write
   * that changes it. GET is a session read, like the playlist. POST takes a
   * fresh authenticator, like a publish: it signs the same removals document
   * the crew tablet writes.
   */
  adminRemovals: "/v1/admin/kiosk/removals",
  /**
   * How long a hosted film is. nova_admin session. The desk cannot open a
   * client's CDN, so the length is read here and written onto the piece.
   */
  filmLength: "/v1/admin/kiosk/film-length",
  /**
   * Sign the Game board into the row a screen poll serves. Armed admin
   * only: the tablet API cannot hold the key. A signed-in nova_admin or
   * the admin token can ask for one pass. Cloud Run gives an idle
   * instance no CPU, so the 10s interval in routes.ts is not a hall
   * clock — a stale poll, or this url, is.
   */
  liveBoard: "/v1/admin/kiosk/live-board",

  /** Take something off a wall, or put it back. A pull token. */
  pull: "/v1/kiosk/removals",
  /** What a screen polls. A view token. */
  screen: "/v1/kiosk/screen",
  /** The bytes of one asset. No credential: the id is the capability. */
  asset: "/v1/kiosk/assets/:asset_id",
  /** The bytes of one film. No credential: the id is the capability, as for a picture. */
  film: "/v1/kiosk/films/:film_id",
} as const;

/** The role the screen-facing transactions run as. Never the service's own. */
export const KIOSK_ROLE = "xp_kiosk";

/**
 * How often a screen is expected to ask, and therefore what the limiter has to
 * allow.
 *
 * Three seconds is the urgent channel's whole latency budget and it is chosen
 * here rather than on the screen, because a number the server enforces and a
 * number the client believes have to be the same number. It is sent back in
 * every answer as `poll_after_ms`, so a screen built last month against a
 * shorter interval is slowed down by the server rather than by a rebuild.
 *
 * WHY IT IS NOT LONGER. This is the only channel that gets a photograph off a
 * wall. A minute of a slur on a two-metre screen is a minute somebody is
 * standing in front of.
 *
 * WHY IT IS NOT SHORTER. The answer is a database round trip, and a second
 * would treble the load for a second of latency nobody standing at a stand can
 * distinguish from none.
 */
export const SCREEN_POLL_MS = 3_000;

/**
 * How long an answer is good for, sent to the screen as `lease_ms`.
 *
 * Fifteen minutes is three hundred missed polls, which is not a slow network:
 * it is a screen that has been cut off. What a screen does when the lease runs
 * out is kiosk/src/transport/screen.ts' decision and is the whole of this
 * design's answer to "a screen showing withdrawn content" — it is not the same
 * failure as a screen showing yesterday's photographs and it is not treated as
 * one.
 */
export const SCREEN_LEASE_MS = 15 * 60_000;

/**
 * Connected screens' columns, each sortable on the server (12 Oct hand-off:
 * every column sorts): Screen, Last seen, Version, Pull, Issues. Here beside
 * the url, importing nothing, so the console names the keys the route takes.
 */
export const KIOSK_SEEN_SORT_KEYS = ["screen", "seen", "version", "pull", "issues"] as const;
export type KioskSeenSortKey = (typeof KIOSK_SEEN_SORT_KEYS)[number];
